import React from 'react';
import { Text, TextAs, TextSizes, Tooltip } from '@clockout-dev/design-elements';
import styles from './styles/reports-funnel-metric-card.module.scss';

/**
 * One card of the enrollment funnel (Figma node 5693:16365).
 *
 * Layout matches the design 1:1 — three centered rows stacked vertically:
 *   1. Uppercase, letter-spaced step label (slate-400). When the server
 *      supplies a `definition`, the label carries a help-cursor tooltip
 *      with it (dotted underline cue, keyboard-focusable).
 *   2. Large bold count in deep navy. `…` while loading; `—` means exactly
 *      one thing — the value is NOT LOADED (its source failed) — and says so
 *      in a tooltip. A real zero renders `0`.
 *   3. Delta label row. Always rendered (an aria-hidden nbsp when empty) so
 *      every card in a row keeps the same height.
 *
 * The `variant` prop picks the card fill (white → blue-50 → blue-100 →
 * blue-200 → primary blue) so the funnel reads as a single composition
 * that gets darker the closer the step is to the terminal conversion card.
 * The `primary` variant flips every text color to white, so the `tone` is
 * visually absorbed there (the server sends `success` for it — expected).
 *
 * The card takes no `percent` / `direction`: `deltaLabel` is the display
 * string and `tone` is authoritative — both computed server-side.
 *
 * Cards are filters (KAN-583): when `clickable`, the card IS a `<button>`
 * (Enter, Space and focus for free) that fires `onClick(stageKey)`; `active`
 * renders the selected state and `aria-pressed`. The accessible name is the
 * visible content (label, count, delta), so a screen reader hears the number
 * too. A non-filterable card is a static tile — a plain `<div>`, no handler,
 * no hover affordance — never a dead button. `mismatch` renders a small
 * dev-only badge when the table total disagrees with the count.
 *
 * @param {Object} props
 * @param {string} [props.label]  - absent while loading (label-free skeleton)
 * @param {number | null | undefined} props.count  - null = not loaded; 0 is a real zero
 * @param {string} [props.deltaLabel]
 * @param {'neutral' | 'warn' | 'danger' | 'success'} [props.tone]
 * @param {'default' | 'surface_50' | 'surface_100' | 'surface_200' | 'primary'} [props.variant]
 * @param {string | null} [props.definition] - server-authored tooltip: what the
 *   card counts, against what base, over what period
 * @param {boolean} [props.loading]
 * @param {boolean} [props.clickable]
 * @param {boolean} [props.active]
 * @param {string|null} [props.stageKey]  - the card key sent as `?funnelStage=`
 * @param {(stageKey: string) => void} [props.onClick]
 * @param {{ cardCount: number, tableCount: number } | null} [props.mismatch]
 */
function ReportsFunnelMetricCard({
    label,
    count,
    deltaLabel,
    tone = 'neutral',
    variant = 'default',
    definition = null,
    loading = false,
    clickable = false,
    active = false,
    stageKey = null,
    onClick,
    mismatch = null,
}) {
    const variantClass = styles[`variant_${variant}`] || styles.variant_default;
    const toneClass = variant === 'primary' ? '' : (styles[`tone_${tone}`] || styles.tone_neutral);
    const notLoaded = !loading && count == null;
    const isButton = clickable && !loading && typeof onClick === 'function' && !!stageKey;
    const showMismatch = !!mismatch && process.env.NODE_ENV !== 'production';

    const activate = () => {
        if (isButton) onClick(stageKey);
    };

    const labelNode = loading ? (
        <span className={styles.labelSkeleton} aria-hidden data-testid="funnel-card-label-skeleton" />
    ) : (
        <Text
            as={TextAs.SPAN}
            size={TextSizes.EXTRA_SMALL}
            className={`${styles.label} ${definition ? styles.labelCue : ''}`}
            title={definition ? undefined : label}
            tabIndex={definition ? 0 : undefined}
        >
            {label}
        </Text>
    );

    const countNode = (
        <Text
            as={TextAs.SPAN}
            size={TextSizes.DISPLAY_SM}
            className={styles.count}
            aria-label={notLoaded ? 'Not loaded' : undefined}
        >
            {loading ? '…' : (notLoaded ? '—' : Number(count).toLocaleString('en-US'))}
        </Text>
    );

    const classNames = [
        styles.card,
        variantClass,
        loading ? styles.loading : '',
        isButton ? styles.clickable : '',
        active ? styles.active : '',
    ]
        .filter(Boolean)
        .join(' ');

    // A native button when the card filters, a div otherwise — same markup
    // inside, so the two read identically and the grid never shifts.
    const Root = isButton ? 'button' : 'div';

    return (
        <Root
            className={classNames}
            data-testid="funnel-card"
            type={isButton ? 'button' : undefined}
            aria-pressed={isButton ? active : undefined}
            data-stage-key={stageKey || undefined}
            data-active={active ? 'true' : undefined}
            onClick={isButton ? activate : undefined}
        >
            {definition && !loading ? (
                // `Text` forwards its ref to the DOM node, so the tooltip can
                // anchor without an extra wrapper (same pattern as the billing
                // KPI cards' "prior period" cue).
                <Tooltip title={definition} placement="bottom">
                    {labelNode}
                </Tooltip>
            ) : (
                labelNode
            )}
            {notLoaded ? (
                <Tooltip title="Not loaded — this metric is currently unavailable" placement="bottom">
                    {countNode}
                </Tooltip>
            ) : (
                countNode
            )}
            <Text
                as={TextAs.SPAN}
                size={TextSizes.EXTRA_SMALL}
                className={`${styles.delta} ${toneClass}`}
                aria-hidden={deltaLabel && !loading ? undefined : true}
                data-testid="funnel-card-delta"
            >
                {deltaLabel && !loading ? deltaLabel : ' '}
            </Text>
            {showMismatch ? (
                <span
                    className={styles.mismatch}
                    title={`Card counts ${mismatch.cardCount}, table returned ${mismatch.tableCount}`}
                    data-testid="funnel-card-mismatch"
                >
                    table {Number(mismatch.tableCount).toLocaleString('en-US')}
                </span>
            ) : null}
        </Root>
    );
}

export default ReportsFunnelMetricCard;
